/** Web Audio ringtone (callee) and ringback (caller) — no external assets. */

type ToneHandle = { start: () => void; stop: () => void };

function getCtx(): AudioContext | null {
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    return new Ctx();
  } catch {
    return null;
  }
}

function playTone(
  ctx: AudioContext,
  freq: number,
  start: number,
  duration: number,
  gain = 0.15
): void {
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.value = freq;
  g.gain.setValueAtTime(0, start);
  g.gain.linearRampToValueAtTime(gain, start + 0.02);
  g.gain.linearRampToValueAtTime(0, start + duration);
  osc.connect(g);
  g.connect(ctx.destination);
  osc.start(start);
  osc.stop(start + duration + 0.05);
}

/** Callee: classic phone ring pattern. */
export function createRingtone(): ToneHandle {
  let ctx: AudioContext | null = null;
  let intervalId: ReturnType<typeof setInterval> | null = null;
  let stopped = false;

  const ring = () => {
    if (!ctx || stopped) return;
    const t = ctx.currentTime;
    playTone(ctx, 440, t, 0.4, 0.2);
    playTone(ctx, 480, t + 0.45, 0.4, 0.2);
    playTone(ctx, 440, t + 0.9, 0.4, 0.2);
    playTone(ctx, 480, t + 1.35, 0.4, 0.2);
  };

  return {
    start() {
      stopped = false;
      ctx = getCtx();
      if (!ctx) return;
      void ctx.resume();
      ring();
      intervalId = setInterval(ring, 3000);
    },
    stop() {
      stopped = true;
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
      void ctx?.close();
      ctx = null;
    },
  };
}

/** Caller: US-style ringback beeps while waiting. */
export function createRingback(): ToneHandle {
  let ctx: AudioContext | null = null;
  let intervalId: ReturnType<typeof setInterval> | null = null;
  let stopped = false;

  const beep = () => {
    if (!ctx || stopped) return;
    const t = ctx.currentTime;
    playTone(ctx, 425, t, 1.0, 0.12);
    playTone(ctx, 425, t + 1.4, 1.0, 0.12);
  };

  return {
    start() {
      stopped = false;
      ctx = getCtx();
      if (!ctx) return;
      void ctx.resume();
      beep();
      intervalId = setInterval(beep, 4000);
    },
    stop() {
      stopped = true;
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
      void ctx?.close();
      ctx = null;
    },
  };
}
